import { Check } from 'lucide-react';
import { useTranslation } from '@/i18n';
import {
  useNetzplanImportStore,
  WIZARD_STEPS,
  type WizardStep,
} from '@/store/useNetzplanImportStore';
import { UploadStep } from './components/UploadStep';
import { CustomerStep } from './components/CustomerStep';
import { ObjectStep } from './components/ObjectStep';
import { TaskStep } from './components/TaskStep';
import { CommitStep } from './components/CommitStep';

const STEP_INDEX: Record<WizardStep, number> = {
  upload: 0, customers: 1, objects: 2, tasks: 3, commit: 4,
};

/**
 * Guided migration of the legacy "Netzplan aller Objekte" workbook.
 *
 * Upload → verify Customers → Objects → Tasks → Commit. Each step lets the user link to
 * an existing record, create a new one, or skip. The commit builds standard import
 * workbooks in-browser and drives the existing per-entity import/excel pipeline.
 */
export default function NetzplanImportPage() {
  const { t } = useTranslation('import');
  const { step, setStep, staging } = useNetzplanImportStore();
  const current = STEP_INDEX[step];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('title')}</h1>
        <p className="text-[13px] text-slate-500 mt-1">{t('subtitle')}</p>
      </div>

      <nav className="flex flex-wrap items-center gap-1.5">
        {WIZARD_STEPS.map((s, i) => {
          const done = i < current;
          const active = i === current;
          // Only allow jumping to a step once the file is parsed (staging present).
          const reachable = staging !== null && i <= current;
          return (
            <button
              key={s}
              type="button"
              disabled={!reachable}
              onClick={() => reachable && setStep(s)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                active
                  ? 'border-[#00A871] bg-emerald-50 text-emerald-800'
                  : done
                    ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    : 'border-slate-200 bg-white text-slate-400'
              } ${reachable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-mono ${
                  done ? 'bg-[#00A871] text-white' : active ? 'bg-[#00A871] text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {done ? <Check className="w-3 h-3" strokeWidth={3} /> : i + 1}
              </span>
              {t(`steps.${s}`)}
            </button>
          );
        })}
      </nav>

      {step === 'upload' && <UploadStep />}
      {step === 'customers' && <CustomerStep />}
      {step === 'objects' && <ObjectStep />}
      {step === 'tasks' && <TaskStep />}
      {step === 'commit' && <CommitStep />}
    </div>
  );
}
